/**
 * Theme selection.
 *
 * The palette in styles/tokens.css already defines three states:
 *
 *   - light   :root                              (the bare default)
 *   - dark    :root[data-theme='dark']           (an explicit choice)
 *   - system  the prefers-color-scheme media query, which only takes effect
 *             while no data-theme attribute is present — it is written as
 *             :root:not([data-theme='light']) so an explicit light choice wins
 *             over a dark OS.
 *
 * So "apply a theme" is exactly "set, or clear, the data-theme attribute on
 * <html>". This module is the one place that decides what goes on that
 * attribute; the toggle button and the pre-paint bootstrap in index.html both
 * speak in these same three values and the same storage key.
 */

export type ThemeChoice = 'light' | 'dark' | 'system'

/** Kept in sync with the inline bootstrap in index.html. */
export const THEME_STORAGE_KEY = 'ereg-theme'

/**
 * The look someone gets before they have chosen anything.
 *
 * Dark, since 2026-09-24: the deep navy ground with the orange rail item is
 * the design the product owner picked, and it is what the application is
 * drawn for first. Light is still one click away and still complete. The
 * inline bootstrap in index.html applies the same default before first paint.
 */
export const DEFAULT_THEME: ThemeChoice = 'dark'

function isThemeChoice(value: unknown): value is ThemeChoice {
  return value === 'light' || value === 'dark' || value === 'system'
}

/** What the user last chose, or the default if they never have. */
export function getStoredTheme(): ThemeChoice {
  try {
    const stored = localStorage.getItem(THEME_STORAGE_KEY)
    if (isThemeChoice(stored)) return stored
  } catch {
    // Private-mode or a locked-down browser can throw on access; fall through
    // to the default rather than breaking the whole app for a preference.
  }
  return DEFAULT_THEME
}

/**
 * Put the choice on <html> and remember it.
 *
 * 'system' removes the attribute entirely, which hands control back to the
 * media query — it is not the same as pinning light.
 */
export function applyTheme(choice: ThemeChoice): void {
  const root = document.documentElement
  if (choice === 'system') root.removeAttribute('data-theme')
  else root.setAttribute('data-theme', choice)

  try {
    localStorage.setItem(THEME_STORAGE_KEY, choice)
  } catch {
    // A theme that cannot be persisted still applies for this session.
  }
}
